<template>
  <div class="lesson-sets">
    <div>
      <h4 class="review-title" @click="viewLesson">
        {{ $t("homework.lesson_sets") }}
        <span> {{ formatLessonsCount(this.lessonSelected.length) }}</span>
        <i class="ml-px-16" v-bind:class="viewLessonSets ? 'elsa-chevron-circle-up' : 'elsa-chevron-circle-down'" />
      </h4>
    </div>
    <elsa-tree
      v-if="viewLessonSets"
      accordion
      icon-class="tree-icon"
      :data="treeData"
      default-expand-all
      v-loading="loading"
      :selected-items="treeSelectedItems"
      v-on:add-remove="handleRemove"
    >
      <template v-slot:title="{ node }">
        <span v-bind:class="node.data.options.titleClassArray">
          {{ node.label }}
        </span>
      </template>
      <template v-slot:subtitle="{ node }">
        <span>{{ $t("homework.lesson_set_levels", [node.data.children.length]) }}</span>
      </template>
      <template v-slot:tags="{ node }">
        <span class="tag">{{ node.data.difficulty_level }}</span>
      </template>
    </elsa-tree>
  </div>
</template>

<script>
import ElsaTree from "@/components/common/Tree";
import { sortBy, sortWith, ascend, prop } from "ramda";
import { getGameTypeIcon } from "@/utils/gameIconFactory";

export default {
  components: {
    ElsaTree,
  },
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    loading: { type: Boolean, default: true },
    removeNode: { type: Boolean, default: false },
  },
  data() {
    return {
      defaultProps: {
        children: "children",
        label: "label",
      },
      expanded: {},
      treeData: [],
      submoduleSelected: [],
      lessonSelected: [],
      topicRendered: [],
      viewLessonSets: true,
    };
  },
  mounted() {
    this.parseData();
  },
  computed: {
    treeSelectedItems() {
      return {
        "level-3": this.submoduleSelected,
        "level-4": this.lessonSelected,
      };
    },
  },
  methods: {
    formatLessonsCount(number) {
      if (number > 1) {
        return `${number} ${this.$t(`common.lessons`)}`;
      } else {
        return `${number} ${this.$t(`common.lesson`)}`;
      }
    },

    parseData() {
      const tree = [];
      let order = 1;
      this.data.map(row => {
        const root = {
          id: row.id,
          label: row.name,
          children: [],
          original_id: row.original_id,
          order: order,
          options: {
            showIcon: true,
            titleClassArray: ["level-title", "root-title"],
          },
        };
        root.children = this.appendModule(row.modules);
        if (root.children.length > 0) {
          tree.push(root);
          order++;
        }
      });
      this.treeData = tree;
    },

    appendModule(modules) {
      modules = sortBy(ascend(prop("order")))(modules);
      const topics = [];
      let order = 1;
      modules.map(module => {
        const isBook = module.submodule_ids[0] == null;
        const label = isBook ? module.topic_name : this.$t("homework.lesson_set_skill", [order]) + module.topic_name;
        const topic = {
          id: module.id,
          label: label,
          children: [],
          order: order,
          original_id: module.topic_original_id,
          options: {
            showIcon: true,
            iconClassArray: ["elsa-level-down"],
            titleClassArray: ["level-title", "level-2-title"],
          },
        };
        if (isBook) {
          topic.children = this.appendBook(modules, module);
        } else {
          topic.children = this.appendSubmodule(module.submodules);
        }
        if (topic.children.length > 0) {
          topics.push(topic);
          order++;
        }
      });
      return topics;
    },

    appendSubmodule(submodules) {
      submodules = sortBy(ascend(prop("order")))(submodules);
      const children = [];
      let order = 1;
      submodules.map(submodule => {
        const prefix = this.$t("homework.lesson_set_level", [order]);
        const newChild = {
          id: submodule.id,
          label: prefix + submodule.name,
          children: [],
          order: order,
          original_id: submodule.original_id,
          children_original_id: [],
          options: {
            showListAction: this.removeNode,
            hideAddAction: false,
            removeNode: this.removeNode,
            showIcon: true,
            iconClassArray: ["elsa-level-down"],
            titleClassArray: ["level-title", "level-3-title"],
            removeActionText: this.$t("homework.lesson_set_remove_all"),
            showConfirm: true,
          },
        };
        this.submoduleSelected.push(submodule.original_id);
        let lessons = sortWith([ascend(prop("order"))])(submodule.lessons);
        lessons.map(lesson => {
          const newSubChild = {
            id: lesson.id,
            label: `${lesson.name} - ${lesson.title}`,
            children: [],
            difficulty_level: lesson.difficulty_level,
            game_type: lesson.game_type,
            original_id: lesson.original_id,
            submodule_id: submodule.original_id,
            options: {
              showListAction: this.removeNode,
              removeNode: this.removeNode,
              showTags: true,
              showIcon: true,
              iconClassArray: [getGameTypeIcon(lesson.game_type), "game-type"],
              titleClassArray: ["level-title", "level-4-title"],
              showConfirm: true,
            },
          };
          newChild.children.push(newSubChild);
          newChild.children_original_id.push(lesson.original_id);
          this.lessonSelected.push(lesson.original_id);
        });
        if (newChild.children.length > 0) {
          children.push(newChild);
          order++;
        }
      });
      return children;
    },

    appendBook(modules, module) {
      const children = [];
      if (this.topicRendered.indexOf(module.topic_original_id) == -1) {
        const topics = modules.filter(item => item.topic_original_id == module.topic_original_id);
        let order = 1;
        topics.map(topic => {
          const newChild = {
            id: topic.id,
            label: topic.name,
            children: [],
            order: order,
            original_id: topic.original_id,
            children_original_id: [],
            options: {
              showListAction: this.removeNode,
              hideAddAction: false,
              removeNode: this.removeNode,
              showIcon: true,
              iconClassArray: ["elsa-level-down"],
              titleClassArray: ["level-title", "level-3-title"],
              removeActionText: this.$t("homework.lesson_set_remove_all"),
              showConfirm: true,
            },
          };
          this.submoduleSelected.push(topic.original_id);
          let lessons = sortWith([ascend(prop("order"))])(topic.submodules[0].lessons);
          lessons.map(lesson => {
            const newSubChild = {
              id: lesson.id,
              label: `${lesson.name} - ${lesson.title}`,
              children: [],
              difficulty_level: lesson.difficulty_level,
              game_type: lesson.game_type,
              original_id: lesson.original_id,
              submodule_id: topic.original_id,
              options: {
                showListAction: this.removeNode,
                removeNode: this.removeNode,
                showTags: true,
                showIcon: true,
                iconClassArray: [getGameTypeIcon(lesson.game_type), "game-type"],
                titleClassArray: ["level-title", "level-4-title"],
                showConfirm: true,
              },
            };
            newChild.children.push(newSubChild);
            newChild.children_original_id.push(lesson.original_id);
            this.lessonSelected.push(lesson.original_id);
          });
          if (newChild.children.length > 0) {
            children.push(newChild);
            order++;
          }
        });
        if (children.length > 0) this.topicRendered.push(module.topic_original_id);
      }
      return children;
    },

    handleRemove(node) {
      if (node.level === 3) {
        node.data.children.forEach(item => this.handleRemoveLesson(item.original_id));
        this.handleRemoveSub(node.data.original_id);
      } else if (node.level === 4) {
        this.handleRemoveLesson(node.data.original_id);
      }
    },

    handleRemoveSub(id) {
      let index = this.submoduleSelected.indexOf(id);
      if (index > -1) this.submoduleSelected.splice(index, 1);
    },

    handleRemoveLesson(id) {
      let index = this.lessonSelected.indexOf(id);
      if (index > -1) {
        this.lessonSelected.splice(index, 1);
        this.$emit("remove-lesson", this.lessonSelected);
      }
    },

    // getGameTypeIcon(type) {
    //   let className;
    //   switch (type) {
    //     case "PROGRAM_VIDEO":
    //       className = "elsa-game-type-program-video";
    //       break;
    //     case "LISTEN":
    //       className = "elsa-game-type-listen";
    //       break;
    //     case "CONVERSATION":
    //       className = "elsa-game-type-conversation";
    //       break;
    //     case "PRONUNCIATION":
    //       className = "elsa-game-type-pronunciation";
    //       break;
    //     case "SENTENCE_STRESS":
    //       className = "elsa-game-type-sentence-stress";
    //       break;
    //     case "WORD_STRESS":
    //       className = "elsa-game-type-word-stress";
    //       break;
    //     case "PROGRAM_INSIGHT":
    //       className = "elsa-game-type-program-insight";
    //       break;
    //     case "VIDEO_CONVERSATION":
    //       className = "elsa-game-type-video-conversation";
    //       break;
    //     default:
    //       className = "elsa-game-type";
    //       break;
    //   }
    //   return className;
    // },

    // getTitleByLevel(node) {
    //   let prefix = "";
    //   if (node.level === 1) prefix = this.$t("homework.lesson_set_skill", [node.data.order]);
    //   if (node.level === 2) prefix = this.$t("homework.lesson_set_level", [node.data.order]);
    //   return prefix + node.label;
    // },

    viewLesson() {
      this.viewLessonSets = !this.viewLessonSets;
    },
  },
  watch: {
    data() {
      this.parseData();
    },
  },
};
</script>

<style lang="scss" scoped>
.lesson-sets {
  border: 1px solid #efeff4;
  margin-bottom: 40px;
  border-radius: 12px;

  ::v-deep .el-tree {
    border: unset;
  }

  ::v-deep .el-tree-node__content {
    border-top: 1px solid #e5e5ea;
    border-bottom: unset;
  }
}

.review-title {
  font-size: 20px;
  line-height: 20px;
  font-weight: 600;
  text-transform: uppercase;
  padding: 32px;
  margin: 0;

  span {
    font-size: 14px;
    line-height: 17px;
    background: #007eff;
    border-radius: 100px;
    color: #ffffff;
    padding: 2px 8px;
    margin-left: 1rem;
  }
}

.elsa-chevron-circle-down,
.elsa-chevron-circle-up {
  display: inline-block;
  margin-bottom: -4px;
}

.tag {
  padding: 4px 8px;
  background: #efeff4;
  border-radius: 4px;
  text-transform: uppercase;
  margin-top: 4px;
  font-weight: 600;
  font-size: 12px;
  line-height: 14px;
  letter-spacing: 0.25px;
  font-variant: small-caps;
}

.level-title {
  font-size: 14px;
  line-height: 24px;
  letter-spacing: 0.25px;
  color: #121131;
  font-weight: 400;
}

.root-title {
  font-weight: 600;
  font-size: 20px;
  line-height: 20px;
  /*text-transform: capitalize;*/
  letter-spacing: 0.25px;
  color: #121131;
  font-family: sf-pro-text, sans-serif;
}

.level-2-title {
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #121131;
}

.level-3-title {
  font-weight: 600;
}
</style>
